/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {PHONE_COUNTRIES} from '../constants'

const PHONE_COUNTRIES_BY_DIAL_CODE = [...PHONE_COUNTRIES].sort(
    (firstCountry, secondCountry) => secondCountry.dialCode.length - firstCountry.dialCode.length
)

const E164_PHONE_PATTERN = /^\+[1-9]\d{2,14}$/

/**
 * Returns whether a phone number has an E.164 structure.
 *
 * @param {string} phone - Complete international phone number.
 * @returns {boolean} Whether the value is an E.164 phone number.
 * @example
 * isE164PhoneNumber('+491711234567')
 * // true
 */
export const isE164PhoneNumber = (phone) =>
    typeof phone === 'string' && E164_PHONE_PATTERN.test(phone)

/**
 * Removes national leading zeroes from a phone number.
 *
 * @param {string} value - Local phone number.
 * @returns {string} Phone number without leading zeroes.
 * @example
 * removeLeadingZeroes('001711234567')
 * // '1711234567'
 */
export const removeLeadingZeroes = (value) =>
    typeof value === 'string' ? value.replace(/^0+/, '') : value

/**
 * Returns the country selector and local number content for a stored phone number.
 *
 * @param {string} storedPhone - Phone number saved by the profile API.
 * @param {string} localeCountry - Two-letter country code selected by the locale.
 * @returns {{countryCode: string, value: string}} Country selector and input value.
 * @example
 * parsePhoneNumber('+491711234567', 'DE')
 * // {countryCode: 'DE', value: '1711234567'}
 */
export const parsePhoneNumber = (storedPhone, localeCountry) => {
    const countryCode = localeCountry?.toUpperCase()

    // Missing or invalid stored values use the locale country with an empty input.
    if (typeof storedPhone !== 'string' || !storedPhone) {
        return {countryCode, value: ''}
    }

    // Formatting separators do not affect prefix matching; an international trunk marker is removed.
    let phone = storedPhone
        .trim()
        .replace(/\(\s*0\s*\)/g, '')
        .replace(/[\s().-]/g, '')

    // Whitespace-only, plus-only, and non-phone values use an empty input.
    if (!phone || !/^\+?\d+$/.test(phone)) {
        return {countryCode, value: ''}
    }

    // International numbers written with 00 are matched like numbers written with +.
    if (phone.startsWith('00')) {
        phone = `+${phone.slice(2)}`
    }

    const localeDialCode = PHONE_COUNTRIES_BY_DIAL_CODE.find(
        (phoneCountry) => phoneCountry.countryCode === countryCode
    )?.dialCode

    // Digits beginning with the locale dial code are split into its country and local remainder.
    if (localeDialCode && phone.startsWith(localeDialCode.slice(1))) {
        return {countryCode, value: phone.slice(localeDialCode.length - 1)}
    }

    // Local numbers use the locale country and omit a national leading zero.
    if (!phone.startsWith('+')) {
        return {countryCode, value: removeLeadingZeroes(phone)}
    }

    const matchingCountries = PHONE_COUNTRIES_BY_DIAL_CODE.filter(({dialCode}) =>
        phone.startsWith(dialCode)
    )
    const longestDialCodeLength = Math.max(
        ...matchingCountries.map(({dialCode}) => dialCode.length)
    )
    const countriesWithLongestDialCode = matchingCountries.filter(
        ({dialCode}) => dialCode.length === longestDialCodeLength
    )
    const phoneCountry =
        countriesWithLongestDialCode.find(
            (phoneCountry) => phoneCountry.countryCode === countryCode
        ) || countriesWithLongestDialCode[0]

    // Unknown international prefixes use the locale country without the leading plus sign.
    if (!phoneCountry) {
        return {countryCode, value: phone.slice(1)}
    }

    // Known international prefixes select their country and keep only the local remainder.
    return {
        countryCode: phoneCountry.countryCode,
        value: phone.slice(phoneCountry.dialCode.length)
    }
}
